import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import { useHistory } from 'react-router-dom';
import {
  Calendar as CalendarIcon,
  Check as CheckIcon,
  ChevronDown as ChevronDownIcon,
  ChevronUp as ChevronUpIcon,
  Copy as CopyIcon,
  ExternalLink as ExternalLinkIcon,
  Eye as EyeIcon,
  Home as HomeIcon,
  PlayCircle as PlayCircleIcon,
  PlusCircle as PlusCircleIcon,
  X as CloseIcon,
} from 'react-feather';
import { Store } from 'api/types/store';
import { OnboardingCompleteAction, StoreSource, useAnalytics } from 'analytics';
import { ownerCloseOnboardingPanel } from 'api/owner_sdk';
import ShareModal from 'components/Dashboard/Share/ShareModal';
import DuplicateStore from 'components/Dashboard/Store/StoreModal/Actions/DuplicateStore';
import NewStore from 'components/Dashboard/Store/StoreModal/Actions/NewStore';
import { URLS } from 'components/Routes/urls';
import { Button, Dropdown, IconButton, Link, Menu, MenuItem, Typography } from 'ui-kit';
import { reverse } from 'utils/urls';
import './StoreReadyPanel.scss';

const BASE_CLASS = 'StoreReadyPanel';

const GENERIC_ERROR = 'Something went wrong. Please try again.';

// Airbnb listings are imported through Hospitable Connect, same as the partner list does it
const AIRBNB_URL = '/overview/integrations/landing/hospitableconnect/';
const PARTNERS_URL = '/overview/integrations/';

const HELP_LINKS: {
  id: OnboardingCompleteAction;
  label: string;
  href: string;
  icon: React.ReactNode;
}[] = [
  {
    id: 'live_demo',
    label: 'Watch or attend a demo',
    href: 'https://thehost.co/get-started',
    icon: <PlayCircleIcon />,
  },
  {
    id: 'weekly_qa',
    label: 'Join our weekly Q&A',
    href: 'https://calendly.com/hostcodemo/host-co-office-hours',
    icon: <CalendarIcon />,
  },
  {
    id: 'example_store',
    label: 'See an example store',
    href: 'https://app.thehost.co/store/25804/',
    icon: <EyeIcon />,
  },
];

interface Props {
  store: Store;
  walkthroughLastStep: string | null;
  onClose: () => void;
}

export default function StoreReadyPanel({ store, walkthroughLastStep, onClose }: Props) {
  const history = useHistory();
  const capture = useAnalytics();
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isShareModalOpen, setShareModalOpen] = useState(false);
  const [openStoreModal, setOpenStoreModal] = useState<'new' | 'duplicate' | null>(null);

  const panelProperties = { store_id: store.id, walkthrough_last_step: walkthroughLastStep };

  useEffect(() => {
    capture('onboarding_complete_shown', panelProperties);
  }, []);

  const track = (action: OnboardingCompleteAction) =>
    capture('onboarding_complete_action_clicked', { ...panelProperties, action });

  const handleShare = () => {
    track('share_store');
    setShareModalOpen(true);
  };

  const handleMenuAction = (action: OnboardingCompleteAction) => {
    track(action);
    setMenuOpen(false);

    switch (action) {
      case 'create_new':
        setOpenStoreModal('new');
        break;
      case 'duplicate':
        setOpenStoreModal('duplicate');
        break;
      case 'create_via_airbnb':
        history.push(AIRBNB_URL);
        break;
      case 'create_via_pms':
        history.push(PARTNERS_URL);
        break;
      default:
        break;
    }
  };

  const handleStoreCreated = (created: Store) => {
    // The store page stays mounted across store ids, so the panel has to go with the old store
    onClose();
    history.push(reverse({ url: URLS.STORE_DETAILS, params: { storeId: created.id } }));
  };

  const handleClose = () => {
    track('dismissed');
    ownerCloseOnboardingPanel()
      .then(() => onClose())
      .catch(() => message.error(GENERIC_ERROR));
  };

  const menu = (
    <Menu>
      <MenuItem
        key="create_new"
        icon={<PlusCircleIcon />}
        onClick={() => handleMenuAction('create_new')}
      >
        Create new store
      </MenuItem>
      <MenuItem key="duplicate" icon={<CopyIcon />} onClick={() => handleMenuAction('duplicate')}>
        Duplicate this store
      </MenuItem>
      <MenuItem
        key="create_via_airbnb"
        icon={<HomeIcon />}
        onClick={() => handleMenuAction('create_via_airbnb')}
      >
        Create via Airbnb
      </MenuItem>
      <MenuItem
        key="create_via_pms"
        icon={<ExternalLinkIcon />}
        onClick={() => handleMenuAction('create_via_pms')}
      >
        Create via PMS
      </MenuItem>
    </Menu>
  );

  return (
    <section className={BASE_CLASS}>
      <div className={`${BASE_CLASS}-card`}>
        <span className={`${BASE_CLASS}-card__icon`} aria-hidden="true">
          <CheckIcon />
        </span>
        <div className={`${BASE_CLASS}-card__main`}>
          <Typography variant="h5" className={`${BASE_CLASS}-card__title`}>
            You’re all set!
          </Typography>
          <p className={`${BASE_CLASS}-card__body`}>
            Your store is ready. Take a look around, make any edits, then share it with your guests.
          </p>
          <div className={`${BASE_CLASS}-card__actions`}>
            <Button variant="contained" color="primary" onClick={handleShare}>
              Share your store
            </Button>
            <Dropdown
              visible={isMenuOpen}
              onVisibleChange={setMenuOpen}
              trigger={['click']}
              placement="bottomLeft"
              overlayClassName={`${BASE_CLASS}-menu`}
              overlay={menu}
            >
              <Button
                variant="outlined"
                className={`${BASE_CLASS}-card__create`}
                endIcon={isMenuOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
              >
                Create more stores
              </Button>
            </Dropdown>
          </div>
        </div>
        <aside className={`${BASE_CLASS}-help`}>
          <p className={`${BASE_CLASS}-help__title`}>Need a hand?</p>
          <ul className={`${BASE_CLASS}-help__links`}>
            {HELP_LINKS.map(({ id, label, href, icon }) => (
              <li key={id}>
                {icon}
                <Link href={href} onClick={() => track(id)}>
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </aside>
        <IconButton
          className={`${BASE_CLASS}-card__close`}
          variant="text"
          color="textGray"
          aria-label="Dismiss"
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
      </div>
      <ShareModal
        storeId={store.id}
        isOpen={isShareModalOpen}
        onClose={() => setShareModalOpen(false)}
      />
      <NewStore
        isOpen={openStoreModal === 'new'}
        source={StoreSource.onboarding}
        onCancel={() => setOpenStoreModal(null)}
        onSuccess={handleStoreCreated}
      />
      <DuplicateStore
        isOpen={openStoreModal === 'duplicate'}
        store={store}
        onCancel={() => setOpenStoreModal(null)}
        onSuccess={handleStoreCreated}
      />
    </section>
  );
}
